/**
 * Pure formatting helpers for the Telegram accounts screen (KAN-592).
 * No React, no I/O: everything here is unit-tested in isolation.
 */
import type {
  TelegramAccount,
  TelegramAccountSource,
} from '@/types/telegramAccount';

export const EMPTY_VALUE = '-';

export interface MappedError {
  /** Short technical name shown as is (or a known error class). */
  title: string;
  /** Human-readable Russian explanation, null for unknown errors. */
  hint: string | null;
}

interface KnownError {
  pattern: RegExp;
  title: string;
  hint: string;
}

// Registry: a new known error is a new entry here, not a new branch.
const KNOWN_ERRORS: KnownError[] = [
  {
    pattern: /peerflood/i,
    title: 'PeerFlood',
    hint: 'Telegram ограничил аккаунт за подозрение на спам. Рассылка с него приостановлена.',
  },
  {
    pattern: /floodwait/i,
    title: 'FloodWait',
    hint: 'Превышен лимит частоты запросов Telegram. Нужно подождать, пока ограничение снимется.',
  },
  {
    pattern: /authorization lost/i,
    title: 'authorization lost',
    hint: 'Авторизация аккаунта потеряна. Аккаунт нужно подключить заново.',
  },
];

/** Maps a raw last_error string to a title and a Russian hint. */
export function mapLastError(raw: string): MappedError | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  const known = KNOWN_ERRORS.find((e) => e.pattern.test(text));
  if (known) return { title: known.title, hint: known.hint };
  return { title: text, hint: null };
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Relative date for fresh values, date and time for older ones. */
export function formatLastSent(value: string | null, now: Date): string {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  const diff = now.getTime() - date.getTime();
  if (Number.isNaN(diff)) return EMPTY_VALUE;
  if (diff < MINUTE) return 'только что';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} мин назад`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} ч назад`;
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function isLimitReached(sentToday: number, dailyLimit: number): boolean {
  return dailyLimit > 0 && sentToday >= dailyLimit;
}

const SOURCE_LABELS: Record<TelegramAccountSource, string> = {
  login: 'Вход по коду',
  import: 'Импорт',
};

export function sourceLabel(source: TelegramAccountSource): string {
  return SOURCE_LABELS[source] ?? source;
}

export function outreachLabel(outreach: boolean): string {
  return outreach ? 'Пишет первым' : 'Только отвечает';
}

export function hasMultipleOperators(
  accounts: ReadonlyArray<{ operator_id: string }>
): boolean {
  return new Set(accounts.map((a) => a.operator_id)).size > 1;
}

/**
 * True when a successful send happened after the last error, so the error
 * is stale. Missing, equal or invalid dates never count as stale.
 */
export function isErrorBeforeLastSend(
  lastSentAt: string | null,
  lastErrorAt: string | null
): boolean {
  if (!lastSentAt || !lastErrorAt) return false;
  const sent = new Date(lastSentAt).getTime();
  const err = new Date(lastErrorAt).getTime();
  if (Number.isNaN(sent) || Number.isNaN(err)) return false;
  return sent > err;
}

const CONNECTED_VIA_LABELS: Record<string, string> = {
  code: 'По коду',
  code_2fa: 'По коду и паролю 2FA',
  session_file: 'Файл сессии',
  session_string: 'Строка сессии',
};

const CONNECTED_VIA_UNKNOWN = 'Способ неизвестен';

const CLIENT_PROFILE_LABELS: Record<string, string> = {
  custom: 'Свои ключи',
  desktop: 'Ключи Desktop',
  imported: 'Импорт',
};

const HOLDER_LABELS: Record<string, string> = {
  main: 'Основной контейнер',
  new: 'Новый контейнер',
};

const lookup = (
  labels: Record<string, string>,
  value: string | null | undefined
): string | undefined =>
  value && Object.prototype.hasOwnProperty.call(labels, value)
    ? labels[value]
    : undefined;

/** True when the connection method is one of the known values. */
export function isConnectedViaKnown(value: string | null | undefined): boolean {
  return lookup(CONNECTED_VIA_LABELS, value) !== undefined;
}

export function connectedViaLabel(value: string | null | undefined): string {
  return lookup(CONNECTED_VIA_LABELS, value) ?? CONNECTED_VIA_UNKNOWN;
}

/** Empty string for an empty or unknown profile. */
export function clientProfileLabel(value: string | null | undefined): string {
  return lookup(CLIENT_PROFILE_LABELS, value) ?? '';
}

/** Empty string for an empty or unknown holder. */
export function holderLabel(value: string | null | undefined): string {
  return lookup(HOLDER_LABELS, value) ?? '';
}

type ConnectionFields = Partial<
  Pick<
    TelegramAccount,
    | 'connected_via'
    | 'connected_at'
    | 'last_relogin_at'
    | 'client_profile'
    | 'holder'
  >
>;

/**
 * Absolute date with year, local browser time zone: dd.mm.yyyy hh:mm.
 * Empty string for a missing or invalid value.
 */
export function formatConnectionDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * Secondary lines of the connection cell: "подключён <date>", "перевход <date>",
 * client profile, holder. Dates are shown only for a known method; empty
 * parts are skipped. Dates are absolute (formatConnectionDate).
 */
export function connectionDetails(account: ConnectionFields): string[] {
  const parts: string[] = [];
  if (isConnectedViaKnown(account.connected_via)) {
    const connected = formatConnectionDate(account.connected_at);
    if (connected) parts.push(`подключён ${connected}`);
    const relogin = formatConnectionDate(account.last_relogin_at);
    if (relogin) parts.push(`перевход ${relogin}`);
  }
  const profile = clientProfileLabel(account.client_profile);
  if (profile) parts.push(profile);
  const holder = holderLabel(account.holder);
  if (holder) parts.push(holder);
  return parts;
}

export type StatusColor = 'gray' | 'green' | 'yellow' | 'red';

export interface SettingsEnabledView {
  label: string;
  color: StatusColor;
}

/**
 * The "enabled" flag is a setting, not a connection state: neutral color,
 * explicit wording.
 */
export function settingsEnabledView(enabled: boolean): SettingsEnabledView {
  return {
    label: enabled ? 'Включён в настройках' : 'Выключен в настройках',
    color: 'gray',
  };
}

export interface ConnectionStatusView {
  label: string;
  color: StatusColor;
  known: boolean;
  /** Tooltip, only for the unknown status. */
  hint?: string;
}

const STATUS_VIEWS: Record<string, { label: string; color: StatusColor }> = {
  connected: { label: 'Подключён', color: 'green' },
  connecting: { label: 'Подключается', color: 'yellow' },
  paused: { label: 'Пауза', color: 'yellow' },
  dead: { label: 'Нет авторизации', color: 'red' },
  disabled: { label: 'Отключён', color: 'gray' },
  login_pending: { label: 'Ждёт код', color: 'yellow' },
};

export const STATUS_UNKNOWN_HINT =
  'Живой статус соединения пока не передаётся: его публикует процесс Telegram';

/** Label and color of the live status; missing or unknown value is "unknown". */
export function connectionStatusView(
  status: string | null | undefined,
  statusUntil?: string | null
): ConnectionStatusView {
  const known =
    status && Object.prototype.hasOwnProperty.call(STATUS_VIEWS, status)
      ? STATUS_VIEWS[status]
      : undefined;
  if (!known) {
    return {
      label: 'Неизвестно',
      color: 'gray',
      known: false,
      hint: STATUS_UNKNOWN_HINT,
    };
  }
  let label = known.label;
  if (status === 'paused') {
    const until = formatConnectionDate(statusUntil);
    if (until) label = `${label} до ${until}`;
  }
  return { label, color: known.color, known: true };
}

/** True when the live connection status is known for at least one account. */
export function hasKnownConnectionStatus(
  accounts: ReadonlyArray<{ status?: string | null }>
): boolean {
  return accounts.some((a) => connectionStatusView(a.status).known);
}

// KAN-613: здоровье аккаунта (строка "Состояние" в ячейке статуса)

export interface HealthView {
  label: string;
  color: StatusColor;
  /** true: аккаунт ещё не проверяли, текст курсивом. */
  italic: boolean;
}

const HEALTH_VIEWS: Record<string, { label: string; color: StatusColor }> = {
  ok: { label: 'В порядке', color: 'green' },
  limited: { label: 'Ограничен', color: 'yellow' },
  frozen: { label: 'Заморожен', color: 'red' },
  unknown: { label: 'Не определено', color: 'gray' },
};

const HEALTH_NOT_CHECKED: HealthView = {
  label: 'Не проверен',
  color: 'gray',
  italic: true,
};

/** Подпись и цвет здоровья; пустое или неизвестное значение: "Не проверен". */
export function healthView(health: string | null | undefined): HealthView {
  if (health && Object.prototype.hasOwnProperty.call(HEALTH_VIEWS, health)) {
    return { ...HEALTH_VIEWS[health], italic: false };
  }
  return HEALTH_NOT_CHECKED;
}

/** Подсказка: текст проверки и время (dd.mm.yyyy hh:mm). Пустая строка, если нечего показать. */
export function healthTitle(
  text: string | null | undefined,
  checkedAt: string | null | undefined
): string {
  const parts: string[] = [];
  if (text) parts.push(text);
  const when = formatConnectionDate(checkedAt);
  if (when) parts.push(`проверено ${when}`);
  return parts.join(', ');
}

/** Строка здоровья: только подключённые аккаунты нового держателя, кроме основного. */
export function showsHealth(
  account: Pick<TelegramAccount, 'holder' | 'account_id' | 'status'>
): boolean {
  return (
    account.holder === 'gateway' &&
    account.account_id !== 'primary' &&
    account.status === 'connected'
  );
}

export const CHECK_BUTTON_LABEL = 'Проверить';
export const CHECK_BUTTON_PENDING_LABEL = 'Проверяем...';
export const CHECK_TIMEOUT_MESSAGE =
  'Проверка заняла слишком долго, повторите позже';
export const CHECK_UNAVAILABLE_MESSAGE = 'Держатель сессий недоступен';
export const CHECK_FALLBACK_MESSAGE = 'Не удалось проверить аккаунт';

// Пауза рассылки после ограничения Telegram (PeerFlood / FloodWait)

export const OUTREACH_PAUSE_TITLE =
  'PeerFlood: Telegram ограничил сообщения незнакомым. Аккаунт отвечает на входящие, первым напишет после паузы. Если повторяется, снизьте лимит';

export interface OutreachPauseView {
  label: string;
  title: string;
}

/** Время окончания паузы: "ЧЧ:ММ" сегодня, "завтра" на следующий день, иначе "дд.мм ЧЧ:ММ". */
export function formatPauseUntil(until: Date, now: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const time = `${pad(until.getHours())}:${pad(until.getMinutes())}`;
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(until) - startOfDay(now)) / DAY);
  if (days <= 0) return time;
  if (days === 1) return 'завтра';
  return `${pad(until.getDate())}.${pad(until.getMonth() + 1)} ${time}`;
}

/** Активная пауза рассылки или null (нет поля, прошла, некорректная дата). */
export function outreachPauseView(
  account: Pick<TelegramAccount, 'outreach_paused_until' | 'outreach_pause_reason'>,
  now: Date
): OutreachPauseView | null {
  const raw = account.outreach_paused_until;
  if (!raw) return null;
  const until = new Date(raw);
  if (Number.isNaN(until.getTime()) || until.getTime() <= now.getTime()) return null;
  const when = formatPauseUntil(until, now);
  const label =
    account.outreach_pause_reason === 'peer_flood'
      ? `Telegram ограничил первые сообщения до ${when}`
      : `Пауза Telegram до ${when}`;
  return { label, title: OUTREACH_PAUSE_TITLE };
}

export function isOutreachPaused(
  account: Pick<TelegramAccount, 'outreach_paused_until' | 'outreach_pause_reason'>,
  now: Date = new Date()
): boolean {
  return outreachPauseView(account, now) !== null;
}

export const STATUS_LEGEND =
  'Подключён: сессия жива. Состояние: может ли аккаунт искать и писать (Ограничен и Заморожен: Telegram ограничил аккаунт). Нет авторизации: сессия мертва, нужен новый вход.';
